// src/components/auth/ForgotPasswordPage.tsx
import React, { useState } from 'react';
import { useNotify, useTranslate } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  InputAdornment,
  Paper,
  useTheme,
  useMediaQuery,
  Alert,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { EmailRounded, ArrowBackRounded } from '@mui/icons-material';
import logo from '@/assets/images/roommatik_logo.webp';
import { API_CONFIG } from '@/config/constants';

const LoginContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: theme.custom.login.containerGradient(theme.palette),
  padding: theme.spacing(3),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
  },
}));

const LoginBox = styled(Paper)(({ theme }) => ({
  width: '90%',
  maxWidth: 440,
  borderRadius: theme.custom.radius.large,
  overflow: 'hidden',
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.custom.shadows.login,
  position: 'relative',
  borderTop: `8px solid ${theme.palette.primary.light}`,
  [theme.breakpoints.down('sm')]: {
    borderRadius: theme.custom.radius.large,
    maxWidth: '90%',
  },
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: theme.spacing(8, 4, 4),
  position: 'relative',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(4, 3, 3),
  },
}));

const FormContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(0, 6, 6),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(0, 3, 4),
  },
}));

const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const notify = useNotify();
  const translate = useTranslate();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsLoading(true);

    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok) {
        setEmailSent(true);
        notify(translate('pages.forgot_password.email_sent'), { type: 'success' });
      } else {
        notify(translate('pages.forgot_password.error'), { type: 'error' });
      }
    } catch (error) {
      notify(translate('pages.forgot_password.error'), { type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  const isFormValid = email.length > 0 && email.includes('@');

  return (
    <LoginContainer>
      <LoginBox>
        <LogoContainer>
          <Box
            component="img"
            src={logo}
            alt="Logo de Roommatik"
            sx={{
              width: isMobile ? 120 : 380,
              height: 'auto',
              maxHeight: 90,
              mb: 4,
              filter: theme.custom.login.logoFilter,
              objectFit: 'contain',
            }}
          />
          <Typography variant={isMobile ? "h5" : "h3"}>
            {translate('pages.forgot_password.title')}
          </Typography>
          <Typography variant="body1" sx={{ textAlign: 'center', mt: 1 }}>
            {translate('pages.forgot_password.subtitle')}
          </Typography>
        </LogoContainer>

        <FormContainer>
          {emailSent ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Alert severity="success">
                {translate('pages.forgot_password.success_message')}
              </Alert>
              <Button
                variant="outlined"
                startIcon={<ArrowBackRounded />}
                onClick={handleBackToLogin}
                sx={{
                  width: '100%',
                  py: 1.5,
                  textTransform: 'none',
                }}
              >
                {translate('pages.forgot_password.back_to_login')}
              </Button>
            </Box>
          ) : (
            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                width: '100%',
              }}
            >
              <TextField
                label={translate('pages.forgot_password.email_label')}
                variant="outlined"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                autoFocus
                disabled={isLoading}
                size='medium'
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailRounded />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  width: isMobile ? '100%' : '90%',
                  mb: isMobile ? 2 : 3,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: theme.custom.login.fieldBackground(theme.palette),
                  },
                }}
              />

              <Button
                variant="contained"
                type="submit"
                disabled={isLoading || !isFormValid}
                sx={{
                  width: isMobile ? '100%' : '100%',
                  mt: isMobile ? 1 : 2,
                  py: 2,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                }}
              >
                {isLoading
                  ? translate('pages.forgot_password.sending')
                  : translate('pages.forgot_password.submit_button')
                }
              </Button>

              <Button
                variant="text"
                startIcon={<ArrowBackRounded />}
                onClick={handleBackToLogin}
                disabled={isLoading}
                sx={{
                  mt: 2,
                  textTransform: 'none',
                }}
              >
                {translate('pages.forgot_password.back_to_login')}
              </Button>
            </Box>
          )}
        </FormContainer>
      </LoginBox>
    </LoginContainer>
  );
};

export default ForgotPasswordPage;
